import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider, useTheme } from "@/lib/theme";
import { BrandingProvider } from "@/lib/branding";
import { CurrencyProvider } from "@/lib/currency";
import { useEffect, Component } from "react";
import { registerServiceWorker, subscribeToPush } from "@/lib/pushNotifications";
import { useOfflineSync, installOfflineInterceptors } from "@/hooks/use-offline-sync";
import { useIdleTimeout } from "@/hooks/use-idle-timeout";
import { IdleTimeoutWarning } from "@/components/IdleTimeoutWarning";
import TopNav from "@/components/TopNav";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ProjectList from "@/pages/ProjectList";
import ProjectWizard from "@/pages/ProjectWizard";
import ProjectView from "@/pages/ProjectView";
import SharedProposalView from "@/pages/SharedProposalView";
import PricingRequestProForma from "@/pages/PricingRequestProForma";
import PricingRequestsDashboard from "@/pages/PricingRequestsDashboard";
import ContractorDashboard from "@/pages/ContractorDashboard";
import AdminSettings from "@/pages/AdminSettings";
import Approvals from "@/pages/Approvals";
import Benchmarking from "@/pages/Benchmarking";
import PricingIntelligence from "@/pages/PricingIntelligence";
import MyProfile from "@/pages/MyProfile";
import UserManual from "@/pages/UserManual";
import OnboardingWizard from "@/pages/OnboardingWizard";
import QuickStartGuide from "@/components/QuickStartGuide";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

import { ConfirmProvider } from "@/components/ConfirmDialog";

// Install offline interceptors on module load
installOfflineInterceptors();

class ErrorBoundary extends Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error("App Error:", error, info); }
  render() {
    if (this.state.hasError) return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-bold text-slate-200 mb-2">Something went wrong</h2>
          <p className="text-sm text-slate-400 mb-4">An unexpected error occurred. Please try refreshing the page.</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
            Refresh Page
          </button>
        </div>
      </div>
    );
    return this.props.children;
  }
}


function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function DirectorRoute({ children }) {
  const { user, loading, isDirector } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isDirector) return <Navigate to="/" replace />;
  return children;
}

function TierRoute({ minTier, children }) {
  const { user, loading, tier } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (tier < minTier) return <Navigate to="/" replace />;
  return children;
}

function CreateProjectRoute({ children }) {
  const { user, loading, canCreateProjects } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!canCreateProjects) return <Navigate to="/projects" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading, needsOnboarding } = useAuth();
  const { isOnline, pendingCount, syncing } = useOfflineSync();
  const { showWarning, secondsLeft, stayLoggedIn } = useIdleTimeout();
  const { loadUserTheme } = useTheme();

  // Post-auth setup: load user theme, register service worker
  useEffect(() => {
    if (user) {
      loadUserTheme();
      registerServiceWorker().then(() => {
        // If permission was already granted, silently re-subscribe
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          subscribeToPush();
        }
      });
    }
  }, [user, loadUserTheme]);

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Routes><Route path="/shared/proposal/:shareToken" element={<SharedProposalView />} /><Route path="/p/:token" element={<PricingRequestProForma />} /><Route path="/forgot-password" element={<ForgotPassword />} /><Route path="/reset-password" element={<ResetPassword />} /><Route path="*" element={<Login />} /></Routes>;

  // Auto-redirect Directors to onboarding if setup isn't complete
  if (needsOnboarding) {
    return (
      <>
        <TopNav />
        <main className="app-content animate-fade-in">
          <Routes>
            <Route path="/onboarding" element={<OnboardingWizard />} />
            <Route path="*" element={<Navigate to="/onboarding" replace />} />
          </Routes>
        </main>
      </>
    );
  }

  return (
    <>
      <TopNav />
      {!isOnline && (
        <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-1.5 text-center" data-testid="offline-banner">
          <span className="text-xs font-medium text-amber-400">
            You are offline {pendingCount > 0 && `— ${pendingCount} pending change${pendingCount > 1 ? 's' : ''}`}
            {syncing && ' (syncing...)'}
          </span>
        </div>
      )}
      <QuickStartGuide />
      <main className="app-content animate-fade-in">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/projects" element={<ProjectList />} />
          <Route path="/projects/new" element={<CreateProjectRoute><ProjectWizard /></CreateProjectRoute>} />
          <Route path="/projects/:id/*" element={<ProjectView />} />
          <Route path="/approvals" element={<TierRoute minTier={4}><Approvals /></TierRoute>} />
          <Route path="/benchmarking" element={<DirectorRoute><Benchmarking /></DirectorRoute>} />
          <Route path="/intelligence" element={<DirectorRoute><PricingIntelligence /></DirectorRoute>} />
          <Route path="/contractors" element={<DirectorRoute><ContractorDashboard /></DirectorRoute>} />
          <Route path="/requests" element={<ProtectedRoute><PricingRequestsDashboard /></ProtectedRoute>} />
          <Route path="/shared/proposal/:shareToken" element={<SharedProposalView />} />
          <Route path="/pricing-request/:token" element={<PricingRequestProForma />} />
          <Route path="/p/:token" element={<PricingRequestProForma />} />
          <Route path="/admin" element={<DirectorRoute><AdminSettings /></DirectorRoute>} />
          <Route path="/onboarding" element={<OnboardingWizard />} />
          <Route path="/profile" element={<MyProfile />} />
          <Route path="/manual" element={<UserManual />} />
        </Routes>
      </main>
      <footer className="py-3 px-6 text-center" data-testid="app-footer">
        <p className="text-[11px] text-slate-600 tracking-wide">MEP .Engine is a trademark of FURD Tech Ltd. All rights reserved.</p>
      </footer>
      {showWarning && <IdleTimeoutWarning secondsLeft={secondsLeft} onStayLoggedIn={stayLoggedIn} />}
    </>
  );
}

function App() {
  return (
    <div className="app-layout">
      <BrandingProvider>
        <CurrencyProvider>
        <ThemeProvider>
          <ConfirmProvider>
          <AuthProvider>
            <ErrorBoundary>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
            </ErrorBoundary>
          </AuthProvider>
          </ConfirmProvider>
        </ThemeProvider>
        </CurrencyProvider>
      </BrandingProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
